import { useRef, useState, type ChangeEvent } from 'react';
import { AvatarImage } from './AvatarImage.js';
import { Button } from './Button.js';

/**
 * Reusable image upload control (04 #3) — preview + file picker + remove, shared by the client
 * Account-settings (avatar, company logo) and the admin client editor (logo). It's a thin
 * presentational shell: the caller passes `onSelect(file)` / `onRemove()` (which do the SDK call
 * and any refresh), plus `busy`/`error`/`disabled`. A light client-side pre-check (type + size)
 * gives instant feedback, but the SERVER validation in image-validation.ts is the authority.
 */

const ACCEPT = 'image/jpeg,image/png,image/webp';
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export function ImageUploadField({
  label,
  hint,
  src,
  name,
  hasImage,
  busy = false,
  disabled = false,
  shape = 'circle',
  size = 80,
  error,
  onSelect,
  onRemove,
}: {
  label: string;
  hint?: string;
  /** Serve URL for the current image (include any cache-buster), or null when none. */
  src: string | null;
  /** Fallback initial source (person/company name). */
  name: string;
  /** Whether a stored image currently exists (controls the Remove button). */
  hasImage: boolean;
  busy?: boolean;
  disabled?: boolean;
  shape?: 'circle' | 'square';
  size?: number;
  error?: string | null;
  onSelect: (file: File) => void;
  onRemove?: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const pick = (): void => inputRef.current?.click();

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setLocalError(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setLocalError('Use a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('Image is too large (5 MB maximum).');
      return;
    }
    onSelect(file);
  };

  const shownError = error ?? localError;

  return (
    <div>
      <p className="text-sm font-medium mb-2">{label}</p>
      <div className="flex items-center gap-4">
        <AvatarImage src={src} name={name} size={size} shape={shape} alt={label} />
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={onChange}
              disabled={disabled || busy}
            />
            <Button type="button" size="sm" variant="secondary" onClick={pick} loading={busy} disabled={disabled || busy}>
              {hasImage ? 'Change' : 'Upload'}
            </Button>
            {hasImage && onRemove ? (
              <Button type="button" size="sm" variant="ghost" onClick={onRemove} disabled={disabled || busy}>
                Remove
              </Button>
            ) : null}
          </div>
          {hint ? <p className="text-xs text-[var(--color-muted)]">{hint}</p> : null}
        </div>
      </div>
      {shownError ? <p className="mt-2 text-xs text-[var(--tint-red-text)]">{shownError}</p> : null}
    </div>
  );
}
